import React, { useState, useEffect } from 'react';
import {
  Box,
  CloseButton,
  Collapse,
  Flex,
  HStack,
  Icon,
  Portal,
  Text,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';

// Local storage key prefix for tracking viewed dashboards (persists across sessions)
const STORAGE_KEY_PREFIX = 'dashboard_intro_seen_';

/**
 * DashboardIntroNotification - Shows a brief description notification
 * when a user loads a dashboard for the first time in a session.
 * Modern, transparent glassmorphism UI style.
 * 
 * @param {Object} props
 * @param {string} props.dashboardType - The type/key of the dashboard
 * @param {string} props.title - The dashboard title/name
 * @param {string} props.description - Brief description of what the dashboard shows
 * @param {Object} props.icon - The icon component to display
 * @param {string} props.color - The color scheme for the accent
 */
const DashboardIntroNotification = ({ 
  dashboardType, 
  title, 
  description, 
  icon, 
  color = 'blue' 
}) => {
  const [showNotification, setShowNotification] = useState(false);
  
  // Ultra-transparent glassmorphism styling
  const glassBg = useColorModeValue(
    'rgba(255, 255, 255, 0.6)',
    'rgba(26, 32, 44, 0.6)'
  );
  const borderColor = useColorModeValue(
    'rgba(255, 255, 255, 0.8)',
    'rgba(255, 255, 255, 0.1)'
  );
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  
  // Progress bar track color
  const progressTrackBg = useColorModeValue(
    'rgba(0, 0, 0, 0.05)',
    'rgba(255, 255, 255, 0.1)'
  );
  
  // Icon background - needs to be computed with the color prop
  const iconBgLight = `${color}.50`;
  const iconBgDark = 'rgba(255, 255, 255, 0.1)';
  const iconBg = useColorModeValue(iconBgLight, iconBgDark);
  
  // Responsive width
  const notificationWidth = useBreakpointValue({ base: '280px', md: '320px' });
  
  // Check if this dashboard has been seen before (persists permanently)
  useEffect(() => {
    if (!dashboardType || !description) return;
    
    const storageKey = `${STORAGE_KEY_PREFIX}${dashboardType}`;
    const hasSeenBefore = localStorage.getItem(storageKey);
    
    if (!hasSeenBefore) {
      // Small delay before showing to let the dashboard render first
      const showTimer = setTimeout(() => {
        setShowNotification(true);
        // Mark as seen permanently
        localStorage.setItem(storageKey, 'true');
      }, 600);
      
      // Auto-dismiss after 8 seconds
      const hideTimer = setTimeout(() => {
        setShowNotification(false);
      }, 8600);
      
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [dashboardType, description]);
  
  // Handle manual dismissal
  const handleDismiss = () => {
    setShowNotification(false);
  };
  
  // Don't render if no description provided
  if (!description) return null;
  
  return (
    <Portal>
      <Collapse in={showNotification} animateOpacity>
        <Box
          position="fixed"
          bottom="100px"
          right="24px"
          width={notificationWidth}
          bg={glassBg}
          backdropFilter="blur(20px) saturate(180%)"
          WebkitBackdropFilter="blur(20px) saturate(180%)"
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
          zIndex={1000}
          overflow="hidden"
          transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{ 
            transform: 'scale(1.02)', 
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
          }}
        >
          {/* Gradient accent - thin line at top */}
          <Box
            position="absolute"
            top={0}
            left="20%"
            right="20%"
            height="2px"
            bgGradient={`linear(to-r, transparent, ${color}.400, transparent)`}
            opacity={0.8}
          />
          
          {/* Content */}
          <Box p={4}>
            {/* Header with icon, title, and close button */}
            <Flex justify="space-between" align="flex-start" mb={2}>
              <HStack spacing={3} flex={1}>
                {icon && (
                  <Flex
                    w="28px"
                    h="28px"
                    bg={iconBg}
                    borderRadius="lg"
                    justify="center"
                    align="center"
                    flexShrink={0}
                  >
                    <Icon as={icon} color={`${color}.500`} boxSize="14px" />
                  </Flex>
                )}
                <Text 
                  fontWeight="600" 
                  color={headingColor} 
                  fontSize="13px"
                  letterSpacing="-0.01em"
                  lineHeight="1.3"
                >
                  {title || 'Dashboard'}
                </Text>
              </HStack>
              <CloseButton 
                size="sm"
                color={subtleTextColor}
                onClick={handleDismiss}
                _hover={{ bg: 'blackAlpha.100', color: textColor }}
                ml={2}
                borderRadius="lg"
              />
            </Flex>
            
            {/* Description */}
            <Text 
              color={subtleTextColor} 
              fontSize="12px" 
              lineHeight="1.6"
              pl="40px"
              fontWeight="400"
            >
              {description}
            </Text>
          </Box>
          
          {/* Ultra-subtle progress indicator */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            h="2px"
            bg={progressTrackBg}
            overflow="hidden"
          >
            <Box
              h="100%"
              bgGradient={`linear(to-r, ${color}.400, ${color}.300)`}
              opacity={0.5}
              animation="shrinkWidth 8s linear forwards"
              sx={{
                '@keyframes shrinkWidth': {
                  '0%': { width: '100%' },
                  '100%': { width: '0%' }
                }
              }}
            />
          </Box>
        </Box>
      </Collapse>
    </Portal>
  );
};

/**
 * Helper function to clear all dashboard intro notifications.
 * Useful for testing or when user wants to see intros again.
 */
export const clearDashboardIntros = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

export default DashboardIntroNotification;
